import { Dispatch, SetStateAction, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ProjectApi from "../../../../../api/projectApi";
import { ReactComponent as DeleteIcon } from "../../../../../assets/Companies/delete.svg";
import { ReactComponent as EditIcon } from "../../../../../assets/Companies/edit.svg";
import ConfirmationModal from "../../../../../components/ConfirmationModal/ConfirmationModal";
import { Company, Project } from "../../../../../types/Company";
import { ProjectsAvailableSessionTypes } from "../../../../../types/Projects";
import { formatDateAndTime } from "../../../../../utils/dateActions";
import { getTranslation } from "../../../../../utils/getTranslation";
import { SuccesNotify } from "../../../../../utils/toaster";
import s from "./ProgramCard.module.css";

type ProgramCardProps = {
  programData: Project;
  index: number;
  dataLength: number;
  setProgramsData: Dispatch<SetStateAction<Project[] | null>>;
  onSelectProgram: (program: Project) => void;
  selectedCompany: Company;
};

const ProgramCard = ({
  programData,
  index,
  dataLength,
  setProgramsData,
  selectedCompany,
  onSelectProgram,
}: ProgramCardProps) => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const [deleteProjectModal, setDeleteProjectModal] = useState(false);
  const [actionLoaders, setActionLoaders] = useState({
    deleteProjectLoader: false,
  });
  const discount = useMemo(() => {
    const selectedType = programData.availableSessionTypes?.[0]?.type;
    const pricing =
      selectedType === ProjectsAvailableSessionTypes.PACKAGE
        ? programData.packagePricing
        : programData.pricing;
    const discounts =
      pricing
        ?.map((item) => item.discount)
        .filter((item): item is number => item !== undefined) ?? [];

    if (!discounts.length) return 0;
    return Math.max(...discounts);
  }, [programData]);

  const deleteProgram = async (projectId: string) => {
    if (!token) return;
    setActionLoaders((prev) => ({ ...prev, deleteProjectLoader: true }));
    const response = await ProjectApi.deleteProject(token, projectId);

    setActionLoaders((prev) => ({ ...prev, deleteProjectLoader: false }));
    if (response.status) {
      setProgramsData((prev) =>
        prev ? prev.filter((el) => el._id !== programData._id) : prev
      );
      setDeleteProjectModal(false);
      SuccesNotify("Project has been removed");
    }
  };

  const editProject = () => {
    navigate(
      `/companies/${encodeURIComponent(
        selectedCompany._id,
      )}/projects/${encodeURIComponent(programData._id)}/edit`,
    );
  };

  return (
    <div
      className={s.container}
      style={{
        backgroundColor:
          (index + 1) % 2 === 0 ? "rgba(63, 176, 130, 0.1)" : "rgb(46, 46, 46)",
        borderRadius: index === dataLength - 1 ? "0 0 20px 20px" : "0",
      }}
    >
      <div
        onClick={(e) => {
          e.stopPropagation();
          onSelectProgram(programData);
        }}
        className={s.cardContent}
      >
        <div className={s.nameBlock}>
          {getTranslation(programData.title, "en")}
        </div>
        <div
          className={s.clientsLimitBlock}
          style={{
            color:
              programData.clients.length === programData.clientLimit
                ? "#e55a5a"
                : "var(--primary-color)",
          }}
        >
          {programData.clients.length + "/" + programData.clientLimit}
        </div>
        <div
          className={s.specLimitBlock}
          style={{
            color:
              programData.specialists.length === programData.specialistLimit
                ? "#e55a5a"
                : "var(--primary-color)",
          }}
        >
          {programData.specialists.length + "/" + programData.specialistLimit}
        </div>
        <div
          className={s.discountBlock}
          style={{
            color: discount === 0 ? "#e55a5a" : "var(--primary-color)",
          }}
        >
          {discount === 0 ? 0 : "-" + discount + "%"}
        </div>
        <div className={s.isPrivateBlock}>
          <span
            className={
              programData.isPrivate ? s.privateBlock : s.notPrivateBlock
            }
          >
            {programData.isPrivate ? "Yes" : "No"}
          </span>
        </div>
        <div className={s.endDateBlock}>
          {
            formatDateAndTime(new Date(programData.endDate!), "en")
              .formattedDate
          }
          <div className={s.programActionBlock}>
            <div
              role="button"
              aria-label={`Edit Project ${getTranslation(
                programData.title,
                "en",
              )}`}
              onClick={(e) => {
                e.stopPropagation();
                editProject();
              }}
              className={s.editBtn}
            >
              <EditIcon className={s.editIcon} />
            </div>
            <div
              role="button"
              aria-label={`Delete Project ${getTranslation(
                programData.title,
                "en",
              )}`}
              onClick={(e) => {
                e.stopPropagation();
                setDeleteProjectModal(true);
              }}
              className={s.deleteBtn}
            >
              <DeleteIcon className={s.deleteIcon} />
            </div>
          </div>
        </div>
      </div>
      <ConfirmationModal
        confirmFunc={() => deleteProgram(programData._id)}
        confirmTitle="Are you sure you want to delete this project?"
        modalVisible={deleteProjectModal}
        setModalVisible={setDeleteProjectModal}
        loader={actionLoaders.deleteProjectLoader}
      />
    </div>
  );
};

export default ProgramCard;
